import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { baseApi } from "../baseApi";
import { useLazyQuery } from "../useLazyQuery";
import { SessionStorageService } from "../../services";
import { ResponseCode } from "../types";
import {
  type Product,
  type WTFIRRequest,
  type WTFIRResponse,
  type AuthorizeRequest,
  type AuthorizeResponse,
  type ProfileOptionsResponse,
  type CheckCisEpcRequest,
  type CheckCisEpcResponse,
  type ItemListByZoneRequest,
  type ItemListByZoneResponse,
  type ItemUnsetZoneRequest,
  type ItemUnsetZoneResponse,
  type ShopLoadRequest,
  type ShopLoadResponse,
  type WTFIRRequest_V2,
  type WTFIRResponse_V2,
  type GetActionRequest,
  type GetActionResponse,
  type PairEPCRequest,
  type PairEPCResponse,
  type ItemListRequest,
  type ItemListResponse,
  type ItemSetBlockRequest,
  type ItemSetBlockResponse,
  type CheckSakesRequest,
  type CheckSakesResponse,
  type ListByParentRequest,
  type ListByParentResponse,
  type ListBySeriaRequest,
  type ListBySeriaResponse,
  type LoadByExternalRequest,
  type LoadByExternalResponse,
  type LoadExecutorsRequest,
  type LoadExecutorsResponse,
} from "./types";

export const authorize = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (body: { qrCode: string; deviceId: string }) =>
      baseApi<AuthorizeResponse, AuthorizeRequest>({
        url: "/api/m/profile/authorize",
        method: "POST",
        body: {
          qr_code: body.qrCode,
          device: body.deviceId,
        },
      }),
    onSuccess: (data) => {
      if (data.token) {
        SessionStorageService.setItem("token", data.token);

        client.invalidateQueries({ queryKey: ["userOptions"] });
      }
    },
  });
};

export const wtfirApi = (body: WTFIRRequest) =>
  baseApi<WTFIRResponse, WTFIRRequest>({ url: "/api/m/wtfir", method: "POST", body });
export const useGetWTFIR = () =>
  useLazyQuery<WTFIRResponse, WTFIRRequest>({
    queryKey: (params) => (params ? ["wtfir", params.data, params.input_type] : ["wtfir"]),
    queryFn: wtfirApi,
    staleTime: 0,
  });

export const wtfirApi_v2 = (body: WTFIRRequest_V2) =>
  baseApi<WTFIRResponse_V2, WTFIRRequest_V2>({ url: "/api/m/v2/wtfir", method: "POST", body });
export const useGetWTFIR_V2 = () =>
  useLazyQuery<WTFIRResponse_V2, WTFIRRequest_V2>({
    queryKey: (params) => (params ? ["wtfir", ...params.data, params.input_type] : ["wtfir"]),
    queryFn: wtfirApi_v2,
    staleTime: 0,
  });

export const useGetProfileOptions = () =>
  useLazyQuery<ProfileOptionsResponse, void>({
    queryKey: () => ["profile-options"],
    queryFn: () => baseApi<ProfileOptionsResponse, void>({ url: "/api/m/profile/options", method: "POST" }),
  });

export const useCheckCis = () =>
  useMutation({
    mutationFn: (body: CheckCisEpcRequest) =>
      baseApi<CheckCisEpcResponse, CheckCisEpcRequest>({
        url: "/api/m/check_cis",
        method: "POST",
        body,
      }),
  });

export const itemListByZoneApi = (params: ItemListByZoneRequest) =>
  baseApi<ItemListByZoneResponse, ItemListByZoneRequest>({
    url: "/api/m/item/list_by_zone",
    method: "POST",
    body: params,
  });
export const itemListByZoneWithCursorApi = async (zone_id: string) => {
  let cursor: string = undefined;
  const item_list: Product[] = [];

  do {
    const result = await itemListByZoneApi({ zone_id, cursor });
    if (result.code !== ResponseCode.OK) return;

    cursor = result.cursor;

    item_list.push(...result.items);
  } while (cursor);

  return item_list;
};
export const useItemListByZone = () =>
  useLazyQuery<ItemListByZoneResponse, ItemListByZoneRequest>({
    queryKey: (params) =>
      params ? ["item-list-by-zone", params.zone_id, ...(params.cursor ? [params.cursor] : [])] : ["item-list-by-zone"],
    queryFn: itemListByZoneApi,
    staleTime: 0,
  });

export const useItemUnsetZone = () =>
  useMutation({
    mutationFn: (body: ItemUnsetZoneRequest) =>
      baseApi<ItemUnsetZoneResponse, ItemUnsetZoneRequest>({
        url: "/api/m/item/unset_zone",
        method: "POST",
        body,
      }),
  });

export const shopLoadApi = (body: ShopLoadRequest) =>
  baseApi<ShopLoadResponse, ShopLoadRequest>({ url: "/api/m/shop/load", method: "POST", body });
export const useShopLoad = () =>
  useLazyQuery<ShopLoadResponse, ShopLoadRequest>({
    queryKey: (params) => (params ? ["shop-load", params.shop_id] : ["shop-load"]),
    queryFn: shopLoadApi,
  });

export const useGetAction = () =>
  useLazyQuery<GetActionResponse, GetActionRequest>({
    queryKey: () => undefined,
    queryFn: (params) =>
      baseApi<GetActionResponse, GetActionRequest>({ url: "/api/m/get_action", method: "POST", body: params }),
    staleTime: 0,
  });

export const pairEpcApi = (params: PairEPCRequest) =>
  baseApi<PairEPCResponse, PairEPCRequest>({ url: "/api/m/pair/epc", method: "POST", body: params });
export const usePairEPC = () =>
  useLazyQuery<PairEPCResponse, PairEPCRequest>({
    queryKey: () => undefined,
    queryFn: pairEpcApi,
    staleTime: 0,
  });

export const useItemList = () =>
  useLazyQuery<ItemListResponse, ItemListRequest>({
    queryKey: (params) => (params ? ["item-list", params.article] : ["item-list"]),
    queryFn: (params) =>
      baseApi<ItemListResponse, ItemListRequest>({ url: "/api/m/item/list", method: "POST", body: params }),
    staleTime: 30_000,
  });

export const useItemSetBlock = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (body: ItemSetBlockRequest) =>
      baseApi<ItemSetBlockResponse, ItemSetBlockRequest>({
        url: "/api/m/item/set_block",
        method: "POST",
        body,
      }),
    onSuccess: (_, variables) => {
      client.invalidateQueries({ queryKey: ["short-info-product", variables.item_id] });
    },
  });
};

export const useCheckSales = () =>
  useLazyQuery<CheckSakesResponse, CheckSakesRequest>({
    queryKey: (params) => (params ? ["check-sales", params.cis, params.epc] : ["check-sales"]),
    queryFn: (params) =>
      baseApi<CheckSakesResponse, CheckSakesRequest>({ url: "/api/m/item/check_sales", method: "POST", body: params }),
    staleTime: 0,
  });

export const useListByParent = () =>
  useLazyQuery<ListByParentResponse, ListByParentRequest>({
    queryKey: (params) => (params ? ["list-by-parent", params.parent_guid] : ["list-by-parent"]),
    queryFn: (params) =>
      baseApi<ListByParentResponse, ListByParentRequest>({
        url: "/api/m/item/list_by_parent",
        method: "POST",
        body: params,
      }),
    staleTime: 0,
  });

export const getListBySeriaApi = (params: ListBySeriaRequest) =>
  baseApi<ListBySeriaResponse, ListBySeriaRequest>({ url: "/api/m/item/list_by_seria", method: "POST", body: params });
export const useListBySeria = () =>
  useLazyQuery<ListBySeriaResponse, ListBySeriaRequest>({
    queryKey: (params) => (params ? ["list-by-seria", params.seria_guid] : ["list-by-seria"]),
    queryFn: getListBySeriaApi,
    staleTime: 0,
  });

export const useLoadByExternal = () =>
  useLazyQuery<LoadByExternalResponse, LoadByExternalRequest>({
    queryKey: (params) => (params ? ["load-by-external", ...params.guid] : ["load-by-external"]),
    queryFn: (params) =>
      baseApi<LoadByExternalResponse, LoadByExternalRequest>({
        url: "/api/m/item/load_by_external",
        method: "POST",
        body: params,
      }),
    staleTime: 0,
  });

export const loadExecutorsApi = (params: LoadExecutorsRequest) =>
  baseApi<LoadExecutorsResponse, LoadExecutorsRequest>({ url: "/api/m/executors", method: "POST", body: params });
export const useLoadExecutors = () =>
  useLazyQuery<LoadExecutorsResponse, LoadExecutorsRequest>({
    queryKey: () => ["load-executors"],
    queryFn: loadExecutorsApi,
    staleTime: 0,
  });
